/**
 * interviewPrepShape.js — RC 1.50 R.3
 *
 * Pure mapper that turns a persisted `interview_prep` payload (from
 * `job_scores.reasoning_json.interview_prep`, see backend
 * `ndor/router.py::_empty_interview_prep`) into the operator's
 * 5-bucket structure for the Workspace Interview Prep render.
 *
 * Buckets (operator-locked):
 *   1. generic    — role / fit / decision-context questions
 *   2. technical  — technical questions + scrutiny areas
 *   3. strategic  — positioning recommendations + decision context
 *   4. behavioural — behavioural questions + STAR-anchor scaffolds
 *   5. gap_risk   — gap-probe questions + CV vulnerability points
 *
 * Hard rails:
 *   - Pure function. No I/O. No imports from React / browser APIs.
 *   - Fail-open: null / undefined / non-object inputs → empty
 *     5-bucket skeleton with `generated=false` (so the UI can
 *     render its calm empty state).
 *   - Defensive: every list access tolerates missing or non-array
 *     fields. No throw, no console.warn.
 *   - No charge surface.
 *
 * Schema reference (backend):
 *   {
 *     headline:                    string,
 *     decision_context:            'shortlist' | 'hold' | 'reject' | '',
 *     likely_questions:            [{ question, category }],
 *       // category ∈ { 'technical', 'behavioural', 'fit', 'gap_probe' }
 *     technical_scrutiny_areas:    [{ area, why_probed, preparation_focus }],
 *     behavioural_scrutiny_areas:  [{ area, why_probed, star_anchor_hint }],
 *     cv_vulnerability_points:     [{ vulnerability, likely_recruiter_framing,
 *                                     truthful_response_strategy, soften_alternative? }],
 *     positioning_recommendations: string[],
 *     preparation_priorities:      string[],
 *     generated:                   boolean,
 *     schema_version:              number,
 *   }
 */

const EMPTY_BUCKETS = Object.freeze({
  generic:     { label: 'Generic',     questions: [], areas: [], notes: [] },
  technical:   { label: 'Technical',   questions: [], areas: [], notes: [] },
  strategic:   { label: 'Strategic',   questions: [], areas: [], notes: [] },
  behavioural: { label: 'Behavioural', questions: [], areas: [], notes: [] },
  gap_risk:    { label: 'Gap / Risk',  questions: [], areas: [], notes: [] },
});

function _emptyShape() {
  return {
    headline: '',
    decisionContext: '',
    preparationPriorities: [],
    buckets: {
      generic:     { label: 'Generic',     questions: [], areas: [], notes: [] },
      technical:   { label: 'Technical',   questions: [], areas: [], notes: [] },
      strategic:   { label: 'Strategic',   questions: [], areas: [], notes: [] },
      behavioural: { label: 'Behavioural', questions: [], areas: [], notes: [] },
      gap_risk:    { label: 'Gap / Risk',  questions: [], areas: [], notes: [] },
    },
    generated: false,
    isLegacyOrEmpty: true,
  };
}

/**
 * @param {object|null|undefined} payload  Raw `interview_prep` payload.
 * @returns {{
 *   headline: string,
 *   decisionContext: string,
 *   preparationPriorities: string[],
 *   buckets: Record<'generic'|'technical'|'strategic'|'behavioural'|'gap_risk',
 *     { label: string, questions: Array<object>, areas: Array<object>, notes: string[] }>,
 *   generated: boolean,
 *   isLegacyOrEmpty: boolean,
 * }}
 */
export function mapInterviewPrep(payload) {
  if (!payload || typeof payload !== 'object') return _emptyShape();

  const out = _emptyShape();
  out.headline = typeof payload.headline === 'string' ? payload.headline.trim() : '';
  out.decisionContext = typeof payload.decision_context === 'string'
    ? payload.decision_context.trim().toLowerCase()
    : '';
  out.preparationPriorities = Array.isArray(payload.preparation_priorities)
    ? payload.preparation_priorities.filter((s) => typeof s === 'string' && s.trim())
    : [];

  const likely = Array.isArray(payload.likely_questions) ? payload.likely_questions : [];
  for (const q of likely) {
    if (!q || typeof q !== 'object') continue;
    const text = typeof q.question === 'string' ? q.question.trim() : '';
    if (!text) continue;
    const cat = typeof q.category === 'string' ? q.category.trim().toLowerCase() : '';
    let bucket;
    switch (cat) {
      case 'technical':   bucket = 'technical'; break;
      case 'behavioural': bucket = 'behavioural'; break;
      case 'gap_probe':   bucket = 'gap_risk'; break;
      case 'fit':         bucket = 'generic'; break;
      default:            bucket = 'generic'; // safe fallback
    }
    out.buckets[bucket].questions.push({ question: text, category: cat || 'fit' });
  }

  // Technical scrutiny areas → technical bucket's `areas`
  const techAreas = Array.isArray(payload.technical_scrutiny_areas)
    ? payload.technical_scrutiny_areas
    : [];
  for (const a of techAreas) {
    if (!a || typeof a !== 'object') continue;
    const area = typeof a.area === 'string' ? a.area.trim() : '';
    if (!area) continue;
    out.buckets.technical.areas.push({
      area,
      why: typeof a.why_probed === 'string' ? a.why_probed.trim() : '',
      focus: typeof a.preparation_focus === 'string' ? a.preparation_focus.trim() : '',
    });
  }

  // Behavioural scrutiny areas → behavioural bucket's `areas` (with STAR anchor)
  const behAreas = Array.isArray(payload.behavioural_scrutiny_areas)
    ? payload.behavioural_scrutiny_areas
    : [];
  for (const a of behAreas) {
    if (!a || typeof a !== 'object') continue;
    const area = typeof a.area === 'string' ? a.area.trim() : '';
    if (!area) continue;
    out.buckets.behavioural.areas.push({
      area,
      why: typeof a.why_probed === 'string' ? a.why_probed.trim() : '',
      starAnchor: typeof a.star_anchor_hint === 'string' ? a.star_anchor_hint.trim() : '',
    });
  }

  // CV vulnerability points → gap_risk bucket's `areas`
  const vulns = Array.isArray(payload.cv_vulnerability_points)
    ? payload.cv_vulnerability_points
    : [];
  for (const v of vulns) {
    if (!v || typeof v !== 'object') continue;
    const text = typeof v.vulnerability === 'string' ? v.vulnerability.trim() : '';
    if (!text) continue;
    out.buckets.gap_risk.areas.push({
      vulnerability: text,
      framing: typeof v.likely_recruiter_framing === 'string' ? v.likely_recruiter_framing.trim() : '',
      strategy: typeof v.truthful_response_strategy === 'string' ? v.truthful_response_strategy.trim() : '',
      soften: typeof v.soften_alternative === 'string' ? v.soften_alternative.trim() : '',
    });
  }

  // Positioning recommendations → strategic bucket's `notes`
  out.buckets.strategic.notes = Array.isArray(payload.positioning_recommendations)
    ? payload.positioning_recommendations.filter((s) => typeof s === 'string' && s.trim())
    : [];

  // Determine generated state. Use the upstream flag if present,
  // otherwise derive from any-bucket-has-content.
  const hasAnyContent = Object.values(out.buckets).some(
    (b) => b.questions.length > 0 || b.areas.length > 0 || b.notes.length > 0,
  ) || out.preparationPriorities.length > 0 || !!out.headline;
  out.generated = typeof payload.generated === 'boolean'
    ? !!payload.generated && hasAnyContent
    : hasAnyContent;
  out.isLegacyOrEmpty = !hasAnyContent;
  return out;
}

export const INTERVIEW_PREP_BUCKET_KEYS = Object.freeze([
  'generic', 'technical', 'strategic', 'behavioural', 'gap_risk',
]);

export const INTERVIEW_PREP_EMPTY_BUCKETS = EMPTY_BUCKETS;

export default mapInterviewPrep;
